import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { AlertTriangle, TrendingDown } from "lucide-react";

interface Props {
  estimatedMarginPct: number;
  currentMarginPct: number;
  label?: "committed" | "actual";
}

/**
 * Shows a badge when margin has drifted significantly from estimate.
 * - Yellow "Margin Drift" when current is 3-8 pts below estimated
 * - Red "Margin Critical" when current is >8 pts below estimated
 */
export const MarginAlertBadge = ({ estimatedMarginPct, currentMarginPct, label = "committed" }: Props) => {
  const drift = estimatedMarginPct - currentMarginPct;

  if (drift < 3) return null;

  const isCritical = drift >= 8;
  const text = isCritical ? "Margin Critical" : "Margin Drift";
  const tooltip = `${label === "committed" ? "Committed" : "Actual"} margin is ${drift.toFixed(1)} pts below estimated (${estimatedMarginPct.toFixed(1)}% → ${currentMarginPct.toFixed(1)}%)`;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Badge
          variant="outline"
          className={
            isCritical
              ? "border-destructive text-destructive gap-1 text-[10px]"
              : "border-yellow-500 text-yellow-600 gap-1 text-[10px]"
          }
        >
          {isCritical ? <AlertTriangle className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
          {text}
        </Badge>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-xs text-xs">
        {tooltip}
      </TooltipContent>
    </Tooltip>
  );
};
